import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/authService';
import { quotaService } from '../services/quotaService';
import { DashboardLayout } from '../components/dashboard/DashboardLayout';
import { User, Mail, Save, X, AlertCircle, CheckCircle, TrendingUp, Zap } from 'lucide-react';

export const ProfilePage = () => {
    const { user, loading: authLoading } = useAuth();
    const [isEditing, setIsEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [quotaData, setQuotaData] = useState(null);
    const [loadingQuota, setLoadingQuota] = useState(true);

    // Form state
    const [formData, setFormData] = useState({
        username: '',
        email: ''
    });

    // UI state
    const [message, setMessage] = useState(null); // { type: 'success' | 'error', text: string }
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (user) {
            setFormData({
                username: user.username || '',
                email: user.email || ''
            });
        }
    }, [user]);

    useEffect(() => {
        fetchQuotaData();
        const interval = setInterval(fetchQuotaData, 10000);
        return () => clearInterval(interval);
    }, []);

    const fetchQuotaData = async () => {
        try {
            const data = await quotaService.getQuotaStatus();
            setQuotaData(data);
        } catch (error) {
            console.error('Failed to fetch quota:', error);
        } finally {
            setLoadingQuota(false);
        }
    };

    const validateForm = () => {
        const newErrors = {};

        // Username validation
        if (!formData.username || formData.username.trim().length < 3) {
            newErrors.username = 'Username must be at least 3 characters';
        }

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!formData.email || !emailRegex.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validateForm()) {
            return;
        }

        setSaving(true);
        setMessage(null);

        try {
            await authService.updateProfile(formData.username, formData.email);
            setMessage({ type: 'success', text: 'Profile updated successfully!' });
            setIsEditing(false);

            // Refresh user data
            window.location.reload(); // Simple approach - could optimize with context update
        } catch (error) {
            console.error('Profile update failed:', error);

            // Handle different error scenarios
            if (error.response?.status === 404) {
                setMessage({
                    type: 'error',
                    text: 'Profile update not available yet. Backend endpoint not implemented.'
                });
            } else if (error.response?.status === 400) {
                setMessage({
                    type: 'error',
                    text: error.response.data?.detail || 'Invalid profile data'
                });
            } else {
                setMessage({
                    type: 'error',
                    text: 'Failed to update profile. Please try again.'
                });
            }
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        setFormData({
            username: user?.username || '',
            email: user?.email || ''
        });
        setErrors({});
        setMessage(null);
        setIsEditing(false);
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        // Clear error for this field
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: undefined }));
        }
    };

    if (authLoading) {
        return (
            <DashboardLayout>
                <div className="flex items-center justify-center min-h-[400px]">
                    <div className="w-8 h-8 border-4 border-primary-200 border-t-primary-500 rounded-full animate-spin" />
                </div>
            </DashboardLayout>
        );
    }

    // Calculate quota stats
    const dailyUsed = quotaData?.requests_today || 0;
    const dailyLimit = quotaData?.daily_limit || 1000;
    const dailyPercentage = Math.min(Math.round((dailyUsed / dailyLimit) * 100), 100);
    const minuteLimit = quotaData?.per_minute_limit || 15;

    return (
        <DashboardLayout>
            <div className="space-y-4 sm:space-y-6 animate-fade-in max-w-4xl mx-auto">
                {/* Header */}
                <div>
                    <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Profile</h2>
                    <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-1">
                        Manage your account information and view usage statistics
                    </p>
                </div>

                {/* Message Banner */}
                {message && (
                    <div className={`p-4 rounded-lg flex items-center gap-3 ${message.type === 'success'
                        ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-200'
                        : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200'
                        }`}>
                        {message.type === 'success' ? (
                            <CheckCircle className="w-5 h-5 flex-shrink-0" />
                        ) : (
                            <AlertCircle className="w-5 h-5 flex-shrink-0" />
                        )}
                        <span className="flex-1">{message.text}</span>
                        <button
                            onClick={() => setMessage(null)}
                            className="text-current opacity-70 hover:opacity-100"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                )}

                {/* Profile Card */}
                <div className="card">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-xl font-semibold">Personal Information</h3>
                        {!isEditing && (
                            <button
                                onClick={() => setIsEditing(true)}
                                className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-all shadow-glow"
                                style={{ background: 'linear-gradient(135deg, #06B6D4, #3B82F6)' }}
                            >
                                Edit Profile
                            </button>
                        )}
                    </div>

                    <div className="space-y-6">
                        {/* Avatar Section */}
                        <div className="flex items-center gap-4 p-4 rounded-lg bg-gray-50 dark:bg-gray-800/50">
                            <div className="w-20 h-20 rounded-full flex items-center justify-center text-white text-3xl font-bold shadow-glow"
                                style={{ background: 'linear-gradient(135deg, #06B6D4, #3B82F6)' }}>
                                {user?.username?.[0]?.toUpperCase() || 'U'}
                            </div>
                            <div className="flex-1">
                                <div className="text-sm text-gray-600 dark:text-gray-400">Account Status</div>
                                <div className="text-lg font-semibold text-gray-900 dark:text-white">Active</div>
                            </div>
                        </div>

                        {/* Form Fields */}
                        <div className="space-y-4">
                            {/* Username Field */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    <User className="w-4 h-4 inline mr-2" />
                                    Username
                                </label>
                                {isEditing ? (
                                    <div>
                                        <input
                                            type="text"
                                            value={formData.username}
                                            onChange={(e) => handleInputChange('username', e.target.value)}
                                            className={`w-full px-4 py-2 rounded-lg border ${errors.username
                                                ? 'border-red-300 dark:border-red-700'
                                                : 'border-gray-300 dark:border-gray-600'
                                                } bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                                            placeholder="Enter your username"
                                        />
                                        {errors.username && (
                                            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.username}</p>
                                        )}
                                    </div>
                                ) : (
                                    <div className="px-4 py-2 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white">
                                        {user?.username}
                                    </div>
                                )}
                            </div>

                            {/* Email Field */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    <Mail className="w-4 h-4 inline mr-2" />
                                    Email Address
                                </label>
                                {isEditing ? (
                                    <div>
                                        <input
                                            type="email"
                                            value={formData.email}
                                            onChange={(e) => handleInputChange('email', e.target.value)}
                                            className={`w-full px-4 py-2 rounded-lg border ${errors.email
                                                ? 'border-red-300 dark:border-red-700'
                                                : 'border-gray-300 dark:border-gray-600'
                                                } bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                                            placeholder="Enter your email"
                                        />
                                        {errors.email && (
                                            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.email}</p>
                                        )}
                                    </div>
                                ) : (
                                    <div className="px-4 py-2 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white">
                                        {user?.email}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Action Buttons */}
                        {isEditing && (
                            <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t dark:border-gray-700">
                                <button
                                    onClick={handleSave}
                                    disabled={saving}
                                    className="w-full sm:flex-1 px-4 py-3 min-h-[48px] text-white rounded-lg transition-all shadow-glow disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                    style={{ background: 'linear-gradient(135deg, #06B6D4, #3B82F6)' }}
                                >
                                    <Save className="w-4 h-4" />
                                    {saving ? 'Saving...' : 'Save Changes'}
                                </button>
                                <button
                                    onClick={handleCancel}
                                    disabled={saving}
                                    className="w-full sm:w-auto px-4 py-3 min-h-[48px] bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                >
                                    <X className="w-4 h-4" />
                                    Cancel
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Usage Statistics */}
                <div className="card">
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <h3 className="text-xl font-semibold">Usage Statistics</h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                Track your API usage and quota
                            </p>
                        </div>
                        <button
                            onClick={fetchQuotaData}
                            className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-all"
                            title="Refresh Data"
                        >
                            <TrendingUp className={`w-5 h-5 ${loadingQuota ? 'animate-spin' : ''}`} />
                        </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                        {/* Daily Quota */}
                        <div className="p-6 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                            <div className="flex items-center gap-2 mb-2">
                                <TrendingUp className="w-5 h-5 text-blue-600" />
                                <div className="text-sm font-medium text-gray-600 dark:text-gray-400">Daily Quota</div>
                            </div>
                            {loadingQuota ? (
                                <div className="text-3xl font-bold text-blue-600">Loading...</div>
                            ) : (
                                <>
                                    <div className="text-3xl font-bold text-blue-600">
                                        {dailyUsed.toLocaleString()} / {dailyLimit.toLocaleString()}
                                    </div>
                                    <div className="mt-3 h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-blue-600 transition-all duration-300"
                                            style={{ width: `${dailyPercentage}%` }}
                                        />
                                    </div>
                                    <div className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                                        {dailyPercentage}% used today
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Rate Limit */}
                        <div className="p-6 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                            <div className="flex items-center gap-2 mb-2">
                                <Zap className="w-5 h-5 text-green-600" />
                                <div className="text-sm font-medium text-gray-600 dark:text-gray-400">Rate Limit</div>
                            </div>
                            <div className="text-3xl font-bold text-green-600">{minuteLimit} / min</div>
                            <div className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                                Maximum requests per minute
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </DashboardLayout>
    );
};
